import { useEffect } from "react";
import { Modal, Form, Input, Select, InputNumber, Switch } from "antd";

const { TextArea } = Input;

/**
 * SettingsItemForm — Dynamic modal form for any settings section.
 *
 * Renders form fields based on the section config's formFields array.
 *
 * Usage:
 *   <SettingsItemForm
 *     open={isModalOpen}
 *     onClose={() => setModalOpen(false)}
 *     onSubmit={(values) => handleSave(values)}
 *     fields={sectionConfig.formFields}
 *     initialValues={editingRecord}
 *     title="Add Designation"
 *     loading={saving}
 *   />
 */
export default function SettingsItemForm({
  open,
  onClose,
  onSubmit,
  fields = [],
  initialValues = null,
  title = "Add Item",
  loading = false,
}) {
  const [form] = Form.useForm();

  const isEdit = !!initialValues;

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        // Set defaults
        const defaults = {};
        fields.forEach((field) => {
          if (field.defaultValue !== undefined) {
            defaults[field.name] = field.defaultValue;
          }
        });
        form.setFieldsValue(defaults);
      }
    }
  }, [open, initialValues, fields, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values);
      form.resetFields();
    } catch {
      // Validation errors shown by Ant Design
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const renderField = (field) => {
    switch (field.type) {
      case "text":
        return <Input placeholder={`Enter ${field.label.toLowerCase()}`} />;

      case "textarea":
        return <TextArea rows={3} placeholder={`Enter ${field.label.toLowerCase()}`} />;

      case "number":
        return (
          <InputNumber
            style={{ width: "100%" }}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            min={0}
          />
        );

      case "select":
        return (
          <Select
            placeholder={`Select ${field.label.toLowerCase()}`}
            options={field.options}
            allowClear
          />
        );

      case "multiselect":
        return (
          <Select
            mode="multiple"
            placeholder={`Select ${field.label.toLowerCase()}`}
            options={field.options}
            allowClear
          />
        );

      case "boolean":
        return <Switch />;

      default:
        return <Input />;
    }
  };

  return (
    <Modal
      title={isEdit ? `Edit ${title}` : `Add ${title}`}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      destroyOnClose
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 16 }}
      >
        {fields.map((field) => (
          <Form.Item
            key={field.name}
            name={field.name}
            label={field.label}
            rules={
              field.required
                ? [{ required: true, message: `${field.label} is required` }]
                : []
            }
            valuePropName={field.type === "boolean" ? "checked" : "value"}
          >
            {renderField(field)}
          </Form.Item>
        ))}
      </Form>
    </Modal>
  );
}
